import React, { memo, useCallback, useMemo } from "react"
import { graphql, useStaticQuery } from "gatsby"
import { Box } from "@chakra-ui/react"

import { useAppContext } from "@app/providers/app"
import { useCore } from "@app/hooks/useCore"
import { useWishlist } from "@app/hooks/useWishlist"
import { FormButton } from "@app/components/Form/FormButton"
import { Icon } from "@app/components/Icon"

import type { ProductProps, ProductCombinedProps } from "@root/types/global"

type Props = {
  product: ProductProps | ProductCombinedProps
}

export const ProductFormWishlist: React.FC<Props> = memo(({ product }) => {
  const { wishlist } = useStaticQuery<GatsbyTypes.ProductFormWishlistQuery>(graphql`
    query ProductFormWishlist {
      wishlist: sanityPageWishlist {
        additionalAdd
        additionalRemove
      }
    }
  `)
  const { activeVariant } = useAppContext()
  const {
    helpers: { decodeBase64 },
  } = useCore()
  const { existsInWishlist, addToWishlist, deleteFromWishlist, loading } = useWishlist()

  const id = useMemo(() => decodeBase64(product?.id), [decodeBase64, product?.id])
  const variant = useMemo(() => (activeVariant ? activeVariant : product.variants?.[0]), [activeVariant, product.variants])
  const isLoading = useMemo(() => loading !== false, [loading])
  const inWishlist = useMemo(() => existsInWishlist(id), [existsInWishlist, id])

  const handleToggle = useCallback(
    (event: React.BaseSyntheticEvent) => {
      event.preventDefault()

      !inWishlist
        ? addToWishlist({
            ...product,
            selectedSku: variant?.sku,
            selectedTitle: variant?.title,
          })
        : deleteFromWishlist(id)
    },
    [addToWishlist, deleteFromWishlist, id, inWishlist, product, variant]
  )

  return (
    <FormButton
      onClick={handleToggle}
      isDisabled={isLoading}
      title={inWishlist ? wishlist?.additionalAdd : wishlist?.additionalRemove}
      aria-label={inWishlist ? wishlist?.additionalAdd : wishlist?.additionalRemove}
      variant="pdpWishlist"
      h={12}
      w={12}
    >
      <Box pos="relative" mb={0.5} w={12}>
        <Box opacity={!inWishlist ? 1 : 0} transition="opacity 0.2s ease" display="flex" justifyContent={"center"} w={12}>
          <Icon name="heart" width={5} />
        </Box>
        <Box
          inset={0}
          opacity={inWishlist ? 1 : 0}
          pos="absolute"
          transition="opacity 0.2s ease"
          display="flex"
          justifyContent={"center"}
          w={12}
        >
          <Icon name="heart-filled" width={5} />
        </Box>
      </Box>
    </FormButton>
  )
})
